import {
  getStandalonePortalEnvDefaults,
  STANDALONE_EDITABLE_ENABLE_FLAG_KEYS,
} from '../constants/standaloneEnvFlagProfiles.const';
import {
  type EnvFlagBooleanString,
  STANDALONE_ENV_FLAGS_CONST,
  type StandaloneUserType,
  type StandaloneShellEnvFlagDefaults,
  USER_TYPES,
} from '../constants/userTypeSwitch.const';
import { type SupportedEnvSource } from '../config.const';

const normalizeBooleanString = (value: string | undefined): EnvFlagBooleanString | undefined => {
  const normalizedValue = value?.toLowerCase();
  if (normalizedValue === 'true' || normalizedValue === 'false') {
    return normalizedValue;
  }
  return undefined;
};

const getOverrideStorageKey = (userType: StandaloneUserType): string =>
  `${STANDALONE_ENV_FLAGS_CONST.storage.shellOverridePrefix}:${userType}`;

const parseStoredOverrides = (serialized: string | null): StandaloneShellEnvFlagDefaults => {
  if (!serialized) {
    return {};
  }

  try {
    const parsedValue = JSON.parse(serialized) as Record<string, unknown>;
    const validOverrides: StandaloneShellEnvFlagDefaults = {};

    for (const key of STANDALONE_EDITABLE_ENABLE_FLAG_KEYS) {
      const normalizedValue = normalizeBooleanString(
        typeof parsedValue[key] === 'string' ? (parsedValue[key] as string) : undefined,
      );
      if (normalizedValue) {
        validOverrides[key] = normalizedValue;
      }
    }

    return validOverrides;
  } catch {
    return {};
  }
};

export abstract class StandaloneEnvFlagsService {
  static getEditableEnableFlagKeys(): readonly string[] {
    return STANDALONE_EDITABLE_ENABLE_FLAG_KEYS;
  }

  static getShellDefaultOverrides(userType: StandaloneUserType): StandaloneShellEnvFlagDefaults {
    return getStandalonePortalEnvDefaults(userType);
  }

  static getUserTypeOverrides(userType: StandaloneUserType): StandaloneShellEnvFlagDefaults {
    try {
      return parseStoredOverrides(localStorage.getItem(getOverrideStorageKey(userType)));
    } catch {
      return {};
    }
  }

  static setUserTypeOverrides(userType: StandaloneUserType, overrides: StandaloneShellEnvFlagDefaults): void {
    const sanitizedOverrides: StandaloneShellEnvFlagDefaults = {};

    for (const key of STANDALONE_EDITABLE_ENABLE_FLAG_KEYS) {
      const normalizedValue = normalizeBooleanString(overrides[key]);
      if (normalizedValue) {
        sanitizedOverrides[key] = normalizedValue;
      }
    }

    try {
      localStorage.setItem(getOverrideStorageKey(userType), JSON.stringify(sanitizedOverrides));
    } catch {
      // Ignore storage access failures.
    }
  }

  static resetUserTypeOverrides(userType: StandaloneUserType): void {
    try {
      localStorage.removeItem(getOverrideStorageKey(userType));
    } catch {
      // Ignore storage access failures.
    }
  }

  static resetAllOverrides(): void {
    for (const userType of Object.values(USER_TYPES)) {
      this.resetUserTypeOverrides(userType);
    }
  }

  static buildEnvForUserType(userType: StandaloneUserType, baseEnv: SupportedEnvSource): SupportedEnvSource {
    const resolvedEnv = { ...baseEnv };
    const shellDefaults = this.getShellDefaultOverrides(userType);
    const shellOverrides = this.getUserTypeOverrides(userType);

    for (const key of STANDALONE_EDITABLE_ENABLE_FLAG_KEYS) {
      const defaultValue = normalizeBooleanString(shellDefaults[key]);
      const overrideValue = normalizeBooleanString(shellOverrides[key]);

      if (overrideValue) {
        resolvedEnv[key] = overrideValue;
      } else if (defaultValue) {
        resolvedEnv[key] = defaultValue;
      }
    }

    return resolvedEnv;
  }

  static getResolvedFlagValues(
    userType: StandaloneUserType,
    baseEnv: SupportedEnvSource,
  ): Record<string, EnvFlagBooleanString> {
    const resolvedEnv = this.buildEnvForUserType(userType, baseEnv);
    const resolvedFlags: Record<string, EnvFlagBooleanString> = {};

    for (const key of STANDALONE_EDITABLE_ENABLE_FLAG_KEYS) {
      const normalizedValue = normalizeBooleanString(resolvedEnv[key]);
      resolvedFlags[key] = normalizedValue ?? 'false';
    }

    return resolvedFlags;
  }
}
